import React, { useEffect } from "react";
import { Drawer, Form, Input, DatePicker, Button, Select, InputNumber } from 'antd';
import * as actions from "store/actions/AuthActions";
import { useNotification } from 'NotificationContext';
import dayjs from 'dayjs';
import useIsMobile from 'hooks/useIsMobile';

const { TextArea } = Input;
const { Option } = Select;

const AssetMaintenanceForm = ({ visible, onClose, assetId, instance, onSuccess }) => {
    const [form] = Form.useForm();
    const notify = useNotification();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (visible) {
            if (instance) {
                const data = { ...instance };
                if (data.maintenance_date) {
                    data.maintenance_date = dayjs(data.maintenance_date);
                }
                if (data.completed_date) {
                    data.completed_date = dayjs(data.completed_date);
                }
                form.setFieldsValue(data);
            } else {
                form.resetFields();
                form.setFieldsValue({
                    maintenance_date: dayjs(),
                    maintenance_type: 'REPAIR'
                });
            }
        }
    }, [visible, instance, form]);

    const handleSubmit = async (values) => {
        const formattedValues = { ...values };
        formattedValues.asset = assetId;

        if (formattedValues.maintenance_date) {
            formattedValues.maintenance_date = dayjs(formattedValues.maintenance_date).format('YYYY-MM-DD');
        }
        if (formattedValues.completed_date) {
            formattedValues.completed_date = dayjs(formattedValues.completed_date).format('YYYY-MM-DD');
        }

        try {
            if (instance) {
                const response = await actions.updateAssetMaintenance(formattedValues, instance.id);
                if (response) {
                    notify("success", "Maintenance record updated successfully");
                    onSuccess();
                }
            } else {
                const response = await actions.addAssetMaintenance(formattedValues);
                if (response && response.status === 201) {
                    notify("success", "Maintenance record created successfully");
                    onSuccess();
                }
            }
        } catch (error) {
            notify("error", "Failed to save maintenance record");
        }
    };

    return (
        <Drawer
            title={instance ? "Edit Maintenance Record" : "Add Maintenance Record"}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 500}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button type="primary" onClick={() => form.submit()} style={isMobile ? { flex: 1 } : undefined}>
                        Save
                    </Button>
                </div>
            }
        >
            <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item
                    label="Maintenance Type"
                    name="maintenance_type"
                    rules={[{ required: true, message: 'Please select maintenance type' }]}
                >
                    <Select>
                        <Option value="REPAIR">Repair</Option>
                        <Option value="UPGRADE">Upgrade</Option>
                        <Option value="INSPECTION">Inspection</Option>
                        <Option value="CLEANING">Cleaning</Option>
                        <Option value="OTHER">Other</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Description"
                    name="description"
                    rules={[{ required: true, message: 'Please enter description' }]}
                >
                    <TextArea rows={4} placeholder="Describe the maintenance work..." />
                </Form.Item>

                <Form.Item
                    label="Maintenance Date"
                    name="maintenance_date"
                    rules={[{ required: true, message: 'Please select maintenance date' }]}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label="Completed Date" name="completed_date">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label="Cost" name="cost">
                    <InputNumber
                        prefix="Rs."
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                    />
                </Form.Item>

                <Form.Item label="Performed By" name="performed_by">
                    <Input placeholder="Name of person/company who performed maintenance" />
                </Form.Item>

                <Form.Item label="Notes" name="notes">
                    <TextArea rows={3} placeholder="Additional notes..." />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default AssetMaintenanceForm;
